import { useEffect, useState } from 'react';
import { useParams } from 'wouter';
import AccessDenied from '@/components/AccessDenied';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import {
  useQuoteChangeDetail,
  useQuoteChangeMutations,
  QUOTE_CHANGE_STATUS_LABELS,
  type QuoteChangeStatus,
} from '@/hooks/useQuoteContract';
import {
  QUOTE_CONTRACT_PERMISSIONS,
  canSeeQuoteContractMenu,
  hasPermission,
} from '@/lib/quoteContractPermissions';

const BADGE: Record<QuoteChangeStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  submitted: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-rose-100 text-rose-800',
  changes_requested: 'bg-amber-100 text-amber-800',
  cancelled: 'bg-zinc-200 text-zinc-700',
};

export default function QuoteChangeDetailPage() {
  const params = useParams<{ id: string }>();
  const id = params.id!;
  const { permissions } = useAuth();
  const canRead = canSeeQuoteContractMenu(permissions);
  const canChangeApprove = hasPermission(
    permissions,
    QUOTE_CONTRACT_PERMISSIONS.CHANGE_APPROVE,
  );

  const { change, reviews, loading, error, fetchChange, setChange } =
    useQuoteChangeDetail();
  const mutations = useQuoteChangeMutations();
  const [comment, setComment] = useState('');
  const [reason, setReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (canRead) void fetchChange(id);
  }, [canRead, id, fetchChange]);

  if (!canRead) return <AccessDenied resource="报价变更" />;
  if (loading && !change) {
    return <Card className="m-6 p-6">加载中…</Card>;
  }
  if (error) {
    return (
      <Card className="m-6 bg-destructive/10 p-6 text-sm text-destructive">
        加载失败：{error.message}
      </Card>
    );
  }
  if (!change) return null;

  const refresh = (next: typeof change) => {
    if (next) setChange(next);
    void fetchChange(id);
  };

  const handleSubmit = async () => {
    setActionError(null);
    const next = await mutations.submit(id, { expectedVersion: change.version });
    if (next) refresh(next);
    else if (mutations.error) setActionError(mutations.error.message);
  };

  const handleApprove = async () => {
    setActionError(null);
    const next = await mutations.approve(id, {
      expectedVersion: change.version,
      reviewerComment: comment || undefined,
    });
    if (next) refresh(next);
    else if (mutations.error) setActionError(mutations.error.message);
  };

  const handleReject = async () => {
    setActionError(null);
    if (!comment.trim()) {
      setActionError('拒绝必须填写复核意见');
      return;
    }
    const next = await mutations.reject(id, {
      expectedVersion: change.version,
      reviewerComment: comment,
    });
    if (next) refresh(next);
    else if (mutations.error) setActionError(mutations.error.message);
  };

  const handleRequestChanges = async () => {
    setActionError(null);
    if (!comment.trim()) {
      setActionError('打回必须填写复核意见');
      return;
    }
    const next = await mutations.requestChanges(id, {
      expectedVersion: change.version,
      reviewerComment: comment,
    });
    if (next) refresh(next);
    else if (mutations.error) setActionError(mutations.error.message);
  };

  const handleCancel = async () => {
    setActionError(null);
    if (!reason.trim()) {
      setActionError('取消必须填写原因');
      return;
    }
    const next = await mutations.cancel(id, {
      expectedVersion: change.version,
      reason,
    });
    if (next) refresh(next);
    else if (mutations.error) setActionError(mutations.error.message);
  };

  let snapshotItems: unknown[] = [];
  try {
    const parsed = JSON.parse(change.proposedItemsSnapshot);
    if (Array.isArray(parsed)) snapshotItems = parsed;
  } catch (_err) {
    snapshotItems = [];
  }

  const editable =
    change.status === 'draft' || change.status === 'changes_requested';

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-xl font-semibold">{change.title}</h1>
          <p className="text-sm text-muted-foreground">
            变更号 {change.changeNo} · 合同 {change.contractId} · 原报价{' '}
            {change.originalQuoteId}
          </p>
        </div>
        <span
          className={`ml-auto rounded-full px-3 py-0.5 text-xs ${BADGE[change.status]}`}
        >
          {QUOTE_CHANGE_STATUS_LABELS[change.status]}
        </span>
      </div>

      {actionError && (
        <Card className="bg-destructive/10 p-3 text-sm text-destructive">
          {actionError}
        </Card>
      )}

      <Card className="space-y-2 p-4 text-sm">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          <div>
            <div className="text-xs text-muted-foreground">建议成交金额</div>
            <div className="font-mono font-semibold">
              {change.proposedTotalAmount}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">建议折扣</div>
            <div className="font-mono">{change.proposedDiscountAmount}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">建议赠送成本</div>
            <div className="font-mono">{change.proposedGiftCost}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">建议毛利</div>
            <div className="font-mono">{change.proposedMargin}</div>
          </div>
        </div>
        <div className="mt-2 rounded-md bg-muted/30 p-2 text-xs">
          变更原因：{change.reason}
        </div>
      </Card>

      <Card className="space-y-2 p-4">
        <h3 className="font-medium">变更后明细 (快照)</h3>
        {snapshotItems.length === 0 ? (
          <p className="text-xs text-muted-foreground">无明细快照</p>
        ) : (
          <pre className="max-h-96 overflow-auto rounded-md bg-muted/30 p-3 text-xs">
            {JSON.stringify(snapshotItems, null, 2)}
          </pre>
        )}
      </Card>

      {canChangeApprove && (
        <Card className="space-y-3 p-4">
          <h3 className="font-medium">审批操作</h3>
          {editable && (
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSubmit} disabled={mutations.loading}>
                提交审批
              </Button>
              <input
                className="rounded-md border bg-background px-3 py-2 text-sm"
                placeholder="取消原因"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={handleCancel}
                disabled={mutations.loading}
              >
                取消
              </Button>
            </div>
          )}
          {change.status === 'submitted' && (
            <div className="space-y-2">
              <textarea
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="审批意见"
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleApprove}
                  disabled={mutations.loading}
                >
                  批准并应用到合同
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleRequestChanges}
                  disabled={mutations.loading}
                >
                  打回修改
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleReject}
                  disabled={mutations.loading}
                >
                  拒绝
                </Button>
              </div>
            </div>
          )}
          {change.status === 'approved' && (
            <p className="text-xs text-muted-foreground">
              变更已通过，合同生效金额已更新；原始合同金额作为历史保留。
            </p>
          )}
        </Card>
      )}

      <Card className="space-y-2 p-4">
        <h3 className="font-medium">审批历史</h3>
        {reviews.length === 0 ? (
          <p className="text-xs text-muted-foreground">暂无审批记录</p>
        ) : (
          <ul className="space-y-1 text-xs">
            {reviews.map((r) => (
              <li key={r.id} className="rounded-md border p-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium">
                    {QUOTE_CHANGE_STATUS_LABELS[r.fromStatus]} →{' '}
                    {QUOTE_CHANGE_STATUS_LABELS[r.toStatus]}
                  </span>
                  <span className="text-muted-foreground">
                    {new Date(r.reviewedAt).toLocaleString('zh-CN')}
                  </span>
                </div>
                <div className="text-muted-foreground">
                  决策：{r.decision} · 操作人 {r.reviewedBy}
                </div>
                {r.reviewerComment && <div>{r.reviewerComment}</div>}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
